import { QueryClient } from "@tanstack/react-query";
import {
  json,
  LoaderFunctionArgs,
  redirect,
  useLoaderData,
} from "react-router-dom";

import { orgStorage } from "@/shared/auth";
import { SearchParams } from "@/shared/environment/SearchParams";
import { RobotoUnauthorizedException } from "@/shared/http";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { Goto } from "@/shared/navigation/Goto";
import { layoutNavigationState } from "@/shared/navigation/layoutNavigation";
import { ErrorMonitoringService } from "@/shared/services";
import { Datasource } from "@/shared/state/visualization";

import {
  EventResolution,
  eventDatasources,
  eventNotices,
  eventSidebarFor,
  resolveEvent,
  resolveEventPanels,
} from "./eventContribution";
import { UnrecoverableError } from "./exceptions";
import {
  resolveByDefaultLayout,
  resolveByLayoutFromNavigationState,
  resolveByLayoutName,
  resolveByWorkspaceId,
} from "./resolvers";
import {
  SessionResolution,
  resolveSessions,
  sessionDatasources,
  sessionDroppedForWorkspaceNotice,
  sessionNotices,
} from "./sessionContribution";
import {
  ConfigSource,
  VisualizerInitContext,
  VisualizerInitData,
  VisualizerNotice,
} from "./visualizerInit";

/**
 * react-router loader for the /visualize route. Resolves the workspace's
 * config source (a saved workspace, a layout, or nothing), the datasources to
 * load, the sidebar view the linked event opens, and any banners to raise.
 *
 * @link https://reactrouter.com/6.30.3/route/loader
 */
export const loader =
  (queryClient: QueryClient) =>
  async ({ request, params }: LoaderFunctionArgs): Promise<Response> => {
    const url = new URL(request.url);

    const httpClient = await makeAuthenticatedHttpClient();
    const orgRecord = orgStorage.getCurrentOrg();

    // Redirect user to select orgs if org not found
    if (!orgRecord) {
      return redirect(`${Goto.route.SignIn}?chooseorg=true`);
    }

    const eventId = url.searchParams.get(SearchParams.EVENT) ?? undefined;
    const sessionIds = [
      ...new Set(url.searchParams.getAll(SearchParams.SESSION)),
    ];

    // A restored workspace carries its own datasources and panels, so fetching
    // the event or listing a session's files would only produce content to
    // discard. Both ids still reach assembleInitData, which raises a notice
    // that they were not applied.
    const restoringWorkspace = params.workspaceId !== undefined;

    const ctx: VisualizerInitContext = {
      eventId: restoringWorkspace ? undefined : eventId,
      httpClient,
      layoutFromNavState: layoutNavigationState.get(),
      layoutName: url.searchParams.get(SearchParams.LAYOUT) ?? undefined,
      orgId: orgRecord.org_id,
      queryClient,
      sessionIds: restoringWorkspace ? [] : sessionIds,
      workspaceId: params.workspaceId,
    };

    try {
      // Fetch concurrently so the event's round trips and the sessions' paged
      // listings do not serialize. Each resource is fetched once; the
      // datasources, the event's panels and sidebar view, and the notices all
      // read from these two resolutions.
      const [eventResolution, sessionResolutions] = await Promise.all([
        resolveEventSafely(ctx),
        resolveSessions(ctx),
      ]);

      const configSource = await resolveConfigSource(ctx, eventResolution);

      const urlDatasources = readUrlDatasources(url.searchParams);

      return json(
        assembleInitData(ctx, configSource, {
          eventId,
          eventResolution,
          sessionIds,
          sessionResolutions,
          urlDatasources,
        }),
      );
    } catch (error) {
      if (error instanceof UnrecoverableError) {
        // Let the error boundary handle this error
        throw error;
      }

      if (error instanceof RobotoUnauthorizedException) {
        // User is authenticated but lacks permission to view this resource
        return redirect(`${Goto.route.Unauthorized}`);
      }

      throw error;
    }
  };

/**
 * Resolve the event named in the URL, treating an unexpected fetch failure as
 * "no event": a transient error loading the linked event must not stop the
 * workspace from opening.
 */
async function resolveEventSafely(
  ctx: VisualizerInitContext,
): Promise<EventResolution | null> {
  try {
    return await resolveEvent(ctx);
  } catch (error) {
    ErrorMonitoringService.captureError(error);
    return null;
  }
}

/**
 * Choose the config source from the first resolver that yields one, in
 * priority order: a shared workspace, a named layout, a handed-off layout,
 * the event's own panels, then the user's default layout.
 *
 * @throws UnrecoverableError when a workspace is missing or its config is invalid
 * @throws RobotoUnauthorizedException when the user may not view the resolved resource
 */
async function resolveConfigSource(
  ctx: VisualizerInitContext,
  eventResolution: EventResolution | null,
): Promise<ConfigSource> {
  const resolvers = [
    () => resolveByWorkspaceId(ctx),
    () => resolveByLayoutName(ctx),
    () => resolveByLayoutFromNavigationState(ctx),
    () => Promise.resolve(resolveEventPanels(eventResolution)),
    () => resolveByDefaultLayout(ctx),
  ];

  for (const resolve of resolvers) {
    try {
      const result = await resolve();
      if (result !== null) {
        return result;
      }
    } catch (error) {
      if (
        error instanceof UnrecoverableError ||
        error instanceof RobotoUnauthorizedException
      ) {
        throw error;
      }

      // Log unexpected errors and fall through to the next resolver.
      ErrorMonitoringService.captureError(error);
    }
  }

  return null;
}

/** The URL's own datasources plus the event and sessions it named, resolved. */
interface ResolvedContributions {
  /**
   * The event the URL named, fetched or not. A restored workspace skips the
   * fetch, leaving `eventResolution` null, so this id is the only record that
   * the URL asked for it.
   */
  eventId: string | undefined;
  eventResolution: EventResolution | null;
  /**
   * The sessions the URL named, listed or not. A restored workspace skips the
   * listing, leaving `sessionResolutions` empty, so these ids are the only
   * record that the URL asked for them.
   */
  sessionIds: string[];
  sessionResolutions: SessionResolution[];
  urlDatasources: Datasource[];
}

/**
 * Assemble the loader's result from the resolved config source, event, and
 * sessions. A restored workspace carries its own datasources and drops both the
 * event and the sessions; every other source loads the URL's, the sessions',
 * and the event's datasources together.
 */
function assembleInitData(
  ctx: VisualizerInitContext,
  configSource: ConfigSource,
  contributions: ResolvedContributions,
): VisualizerInitData {
  const {
    eventId,
    eventResolution,
    sessionIds,
    sessionResolutions,
    urlDatasources,
  } = contributions;

  if (configSource?.type === "workspace") {
    const notices: VisualizerNotice[] = [];
    if (eventId) {
      notices.push(eventDroppedForWorkspaceNotice(ctx.workspaceId, eventId));
    }
    if (sessionIds.length > 0) {
      notices.push(
        sessionDroppedForWorkspaceNotice(ctx.workspaceId, sessionIds),
      );
    }

    return {
      configSource,
      // The restored workspace carries its own datasources.
      datasources: [],
      // No sidebar view: the workspace decides what opens, and the URL's event
      // was never fetched, so there is nothing to show for it.
      notices,
    };
  }

  return {
    configSource,
    datasources: mergeDedupDatasources(
      urlDatasources,
      sessionDatasources(sessionResolutions),
      eventDatasources(eventResolution),
    ),
    eventSidebar: eventSidebarFor(eventResolution),
    notices: [
      ...sessionNotices(sessionResolutions),
      ...eventNotices(eventResolution),
    ],
  };
}

/**
 * The banner shown when a shared workspace opens with an event in the URL:
 * the workspace restores its own data, so the event is not applied. Names the
 * workspace when its id is known.
 */
function eventDroppedForWorkspaceNotice(
  workspaceId: string | undefined,
  eventId: string,
): VisualizerNotice {
  const workspace = workspaceId
    ? `workspace '${workspaceId}'`
    : "a saved workspace";
  return {
    name: "EventDroppedForWorkspaceNotice",
    title: "Event not loaded",
    message: `The URL includes both ${workspace} and event '${eventId}' references, but they are mutually exclusive. The workspace opened with its saved panels and data.`,
    severity: "info",
  };
}

/** Read the datasources named directly in the URL, collapsing duplicates. */
function readUrlDatasources(searchParams: URLSearchParams): Datasource[] {
  const datasetIds = [...new Set(searchParams.getAll(SearchParams.DATASET))];
  const fileIds = [...new Set(searchParams.getAll(SearchParams.FILE))];

  return [
    ...datasetIds.map((datasetId) => ({
      type: "dataset" as const,
      value: { datasetId },
    })),
    ...fileIds.map((fileId) => ({
      type: "file" as const,
      value: { fileId },
    })),
  ];
}

/**
 * Merge the lists in order, keeping the first occurrence of each dataset or
 * file id.
 *
 * Order decides which copy of a duplicated file survives. Callers pass the
 * URL's own files first, so a file that `?file=` and `?session=` both name
 * keeps its unmarked form and is written back to `?file=` on the next rewrite.
 *
 * This settles only the ids a URL arrives with. Adding a file to a workspace
 * already on screen goes through `useAddFilesToWorkspace`, which keeps the
 * datasource that is already there, session-marked or not.
 */
function mergeDedupDatasources(...lists: Datasource[][]): Datasource[] {
  const seen = new Set<string>();
  const merged: Datasource[] = [];

  for (const datasource of lists.flat()) {
    const key =
      datasource.type === "dataset"
        ? `dataset:${datasource.value.datasetId}`
        : `file:${datasource.value.fileId}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    merged.push(datasource);
  }

  return merged;
}

/**
 * react-router shouldRevalidate for the /visualize route.
 *
 * @link https://reactrouter.com/6.30.3/route/should-revalidate
 */
export function shouldRevalidate() {
  // The loader reads the URL (`?event=`, `?dataset=`, `?file=`, `?session=`,
  // `?layout=`) only to seed the initial workspace state. URL changes after
  // that (e.g. adding files to the workspace rewrites `?dataset=`/`?file=`)
  // are read reactively by the workspace, so revalidation is never needed.
  return false;
}

/**
 * Retrieve the loader data for the /visualize route.
 */
export function useWorkspaceLoaderData(): VisualizerInitData {
  return useLoaderData() as VisualizerInitData;
}
